import { HighlightedField } from "./types";
import { groupingOptions, highlightedFields, reportsColors } from './constants';
import { DataGridTypes } from 'devextreme-react/cjs/data-grid';
import { DataGridCell } from 'devextreme/common/export/excel';

const isHighlightedField = (field?: string): field is HighlightedField =>
  !!field && highlightedFields.includes(field as HighlightedField);

export const reportGroupingOptions = groupingOptions.map(
  (e: string, i: number) => {
    return {
      id: i,
      value: e || '',
      text: e || '',
    };
  }
);

const formatPercentage = (numerator: number, denominator: number) => {
  if (!denominator) return '0.00%';
  return `${((numerator / denominator) * 100).toFixed(2)}%`;
};

export const valuesConverter = (data: any[]) => {
  return data.map((row) => {
    let prevData = { ...row };
    prevData.monthYearGroupings?.forEach((e: { name: string; value: any }) => {
      prevData = { ...prevData, [e.name]: e.value };
    });

    return {
      ...prevData,
      totalResponsesPercentage: () =>
        formatPercentage(Number(prevData.totalResponses), Number(prevData.totalRows)),
      detractorPercentage: () =>
        formatPercentage(Number(prevData.detractor), Number(prevData.totalResponses)),
      passivePercentage: () =>
        formatPercentage(Number(prevData.passive), Number(prevData.totalResponses)),
      promotorPercentage: () =>
        formatPercentage(Number(prevData.promotor), Number(prevData.totalResponses)),
    };
  });
};

export const cellPrepared = (e: DataGridTypes.CellPreparedEvent) => {
  if (isHighlightedField(e.column.dataField) && e.rowType === 'data') {
    const fieldName = e.column.dataField;
    let color = reportsColors[fieldName];

    if (fieldName === 'npsScore') {
      const npsScore = Number(e.data?.npsScore ?? e.value ?? 0);
      if (npsScore > 25) {
        color = reportsColors.npsScoreRange1;
      }
      if (npsScore >= 0 && npsScore <= 25) {
        color = reportsColors.npsScoreRange2;
      }
      if (npsScore < 0) {
        color = reportsColors.npsScoreRange3;
      }
    }

    e.cellElement.style.cssText = `background-color: #${color}`;
  }
};

export const customizeCell = ({
  gridCell,
  excelCell,
}: {
  gridCell?: DataGridCell;
  excelCell?: any;
}) => {
  if (isHighlightedField(gridCell?.column?.dataField) && gridCell?.rowType === 'data') {
    const fieldName = gridCell?.column?.dataField;
    let color = reportsColors[fieldName];

    if (fieldName === 'npsScore') {
      const npsScore = Number(gridCell?.data?.npsScore ?? gridCell?.value ?? 0);
      if (npsScore > 25) {
        color = reportsColors.npsScoreRange1;
      }
      if (npsScore >= 0 && npsScore <= 25) {
        color = reportsColors.npsScoreRange2;
      }
      if (npsScore < 0) {
        color = reportsColors.npsScoreRange3;
      }
    }
    excelCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {
        argb: color,
      },
    };
  }
};

const getTotalResponsesPercentage = (rows: any[]) => {
  const grandTotalResponses = rows.reduce(
    (a, b) => a + Number(b.totalResponses || 0),
    0
  );
  const grandTotalSurveys = rows.reduce(
    (a, b) => a + Number(b.totalRows || 0),
    0
  );
  const percentage = (grandTotalResponses / grandTotalSurveys) * 100 || 0;
  return `${percentage.toFixed(2)}%`;
};

const getDetractorPercentage = (rows: any[]) => {
  const grandTotalDetractors = rows.reduce(
    (a, b) => a + Number(b.detractor || 0),
    0
  );
  const grandTotalResponses = rows.reduce(
    (a, b) => a + Number(b.totalResponses || 0),
    0
  );
  const percentage =
    (grandTotalDetractors / grandTotalResponses) * 100 || 0;
  return `${percentage.toFixed(2)}%`;
};

const getPassivePercentage = (rows: any[]) => {
  const grandTotalPassive = rows.reduce(
    (a, b) => a + Number(b.passive || 0),
    0
  );
  const grandTotalResponses = rows.reduce(
    (a, b) => a + Number(b.totalResponses || 0),
    0
  );
  const percentage = (grandTotalPassive / grandTotalResponses) * 100 || 0;
  return `${percentage.toFixed(2)}%`;
};

const getPromotorPercentage = (rows: any[]) => {
  const grandTotalPromotor = rows.reduce(
    (a, b) => a + Number(b.promotor || 0),
    0
  );
  const grandTotalResponses = rows.reduce(
    (a, b) => a + Number(b.totalResponses || 0),
    0
  );
  const percentage = (grandTotalPromotor / grandTotalResponses) * 100 || 0;
  return `${percentage.toFixed(2)}%`;
};

const getNpsScore = (rows: any[]) => {
  const grandTotalResponses = rows.reduce(
    (a, b) => a + Number(b.totalResponses || 0),
    0
  );
  if (grandTotalResponses == 0) return 0;
  const grandTotalPromotor = rows.reduce(
    (a, b) => a + Number(b.promotor || 0),
    0
  );
  const grandTotalDetractor = rows.reduce(
    (a, b) => a + Number(b.detractor || 0),
    0
  );
  const totalPromotor = grandTotalPromotor / grandTotalResponses || 0;
  const totalDetractor = grandTotalDetractor / grandTotalResponses || 0;
  const totalScore = (totalPromotor - totalDetractor) * 100 || 0;
  return Number(totalScore).toFixed(1);
};

export const calculateSelectedRow = (
  options: DataGridTypes.CustomSummaryInfo,
  rows?: any[],
) => {
  if (options.summaryProcess !== 'finalize' || !rows) return;

  switch (options.name) {
    case 'totalResponsesPercentage':
      options.totalValue = getTotalResponsesPercentage(rows);
      break;
    case 'detractorPercentage':
      options.totalValue = getDetractorPercentage(rows);
      break;
    case 'passivePercentage':
      options.totalValue = getPassivePercentage(rows);
      break;
    case 'promotorPercentage':
      options.totalValue = getPromotorPercentage(rows);
      break;
    case 'npsScore':
      options.totalValue = getNpsScore(rows);
      break;
    default:
      break;
  }
};
